import { useCallback, useContext, useMemo, useState } from 'react';

import AccountPickerContext from 'contexts/AccountPickerContext';
import CurrentUserContext from 'contexts/CurrentUserContext';
import getIdentifier from 'getIdentifier';
import getTimeStamp from 'getTimeStamp';
import computeNextSlot from 'lib/reportCadence/computeNextSlot';

const useFormState = ({
  existingSchedule,
  timeZone,
}: {
  existingSchedule: ReportSchedules.Schedule | undefined;
  timeZone: string;
}) => {
  const { id: currentUserId } = useContext(CurrentUserContext);
  const { selectedAccountId } = useContext(AccountPickerContext);

  const [id] = useState<string>(
    existingSchedule ? existingSchedule.id : getIdentifier(),
  );
  const [recipientUserIds, setRecipientUserIds] = useState<string[]>(
    existingSchedule ? existingSchedule.recipientUserIds : [],
  );
  const [recipientGroupIds, setRecipientGroupIds] = useState<string[]>(
    existingSchedule ? existingSchedule.recipientGroupIds ?? [] : [],
  );
  const [notes, setNotes] = useState<string>(
    existingSchedule ? existingSchedule.notes : '',
  );
  const [frequency, setFrequency] = useState<
    ReportSchedules.Cadence['frequency']
  >(existingSchedule ? existingSchedule.cadence.frequency : 'weekly');
  const [timeOfDay, setTimeOfDay] = useState<string>(
    existingSchedule ? existingSchedule.cadence.timeOfDay : '08:00',
  );
  const [dayOfWeek, setDayOfWeek] = useState<number>(
    existingSchedule?.cadence.dayOfWeek ?? 1,
  );
  const [dayOfMonth, setDayOfMonth] = useState<number>(
    existingSchedule?.cadence.dayOfMonth ?? 1,
  );

  const onNotesChanged = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value),
    [],
  );

  const onTimeOfDayChanged = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setTimeOfDay(e.target.value),
    [],
  );

  const cadence = useMemo((): ReportSchedules.Cadence => {
    if (frequency === 'weekly') {
      return { frequency, timeOfDay, dayOfWeek };
    }
    if (frequency === 'monthly') {
      return { frequency, timeOfDay, dayOfMonth };
    }
    return { frequency, timeOfDay };
  }, [dayOfMonth, dayOfWeek, frequency, timeOfDay]);

  const draftSchedule = useMemo(():
    | Omit<ReportSchedules.Schedule, 'savedFilterId'>
    | undefined => {
    if (recipientUserIds.length === 0 && recipientGroupIds.length === 0)
      return undefined;
    if (!timeOfDay.match(/^\d{2}:\d{2}$/)) return undefined;

    const now = getTimeStamp();
    const nextRunAt = computeNextSlot({ cadence, timeZone, after: now });

    return {
      id,
      reportId: '', // filled in by the container
      reportName: '', // filled in by the container
      senderId: currentUserId,
      recipientUserIds,
      recipientGroupIds,
      notes,
      cadence,
      timeZone,
      isActive: true,
      nextRunAt,
      createdBy: existingSchedule ? existingSchedule.createdBy : currentUserId,
      createdOn: existingSchedule ? existingSchedule.createdOn : now,
      updatedBy: currentUserId,
      updatedOn: now,
    };
  }, [
    cadence,
    currentUserId,
    existingSchedule,
    id,
    notes,
    recipientGroupIds,
    recipientUserIds,
    timeOfDay,
    timeZone,
  ]);

  return {
    id,
    recipientUserIds,
    setRecipientUserIds,
    recipientGroupIds,
    setRecipientGroupIds,
    notes,
    onNotesChanged,
    frequency,
    setFrequency,
    timeOfDay,
    onTimeOfDayChanged,
    dayOfWeek,
    setDayOfWeek,
    dayOfMonth,
    setDayOfMonth,
    cadence,
    draftSchedule,
    selectedAccountId,
  };
};

export default useFormState;
